import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Divider,
  Avatar,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { formatCalendarDate } from '../../utils/dateTimeUtils';

export function leaveStatusColor(status) {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
}

export function formatLeaveScope(scope) {
  if (scope === 'specific_batches') return 'Specific batches';
  if (scope === 'full_day') return 'Full day';
  return scope || '—';
}

export function formatLeaveStatus(status) {
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Calendar date only — avoids showing raw ISO timestamps. */
export function formatLeaveDate(dateInput) {
  if (!dateInput) return '—';
  const raw = String(dateInput).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return formatCalendarDate(raw.slice(0, 10));
  }
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return formatCalendarDate(d.toISOString().slice(0, 10));
  }
  return raw;
}

function formatWhen(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function getLeaveTeacherName(request, authUser) {
  if (request?.teacherName) return request.teacherName;
  if (authUser) {
    const full =
      authUser.fullName ||
      [authUser.firstName, authUser.lastName].filter(Boolean).join(' ').trim();
    return full || authUser.email || null;
  }
  return null;
}

function teacherInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

function DetailRow({ label, children }) {
  return (
    <Box sx={{ py: 1.25 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.35 }}>{children}</Box>
    </Box>
  );
}

export function LeaveRequestSummary({ request, showTeacher = false, authUser }) {
  if (!request) return null;
  const batches = request.batches || [];
  const teacherName = getLeaveTeacherName(request, showTeacher ? null : authUser);
  const showNameBlock = showTeacher ? Boolean(request.teacherName) : Boolean(teacherName);

  return (
    <Stack spacing={0} divider={<Divider flexItem />}>
      {showNameBlock ? (
        <DetailRow label="Teacher">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
              {teacherInitials(showTeacher ? request.teacherName : teacherName)}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {showTeacher ? request.teacherName : teacherName}
              </Typography>
              {(showTeacher ? request.teacherEmail : request.teacherEmail || authUser?.email) ? (
                <Typography variant="body2" color="text.secondary">
                  {showTeacher ? request.teacherEmail : request.teacherEmail || authUser?.email}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </DetailRow>
      ) : null}

      <DetailRow label="Leave date">
        <Typography variant="body1" fontWeight={500}>
          {formatLeaveDate(request.leaveDate || request.leave_date)}
        </Typography>
      </DetailRow>

      <DetailRow label="Scope">
        <Typography variant="body1">{formatLeaveScope(request.scope)}</Typography>
      </DetailRow>

      {request.scope === 'specific_batches' && batches.length > 0 ? (
        <DetailRow label="Batches">
          <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
            {batches.map((b) => {
              const resolution =
                b.resolutionType === 'merge'
                  ? 'merged'
                  : b.resolutionType === 'cancel'
                    ? 'cancelled'
                    : b.resolutionType === 'none'
                      ? 'no action'
                      : b.substituteTeacherName
                        ? `sub: ${b.substituteTeacherName}`
                        : null;
              const label = resolution ? `${b.name} (${resolution})` : b.name;
              return <Chip key={b.id || b.name} label={label} size="small" variant="outlined" />;
            })}
          </Stack>
        </DetailRow>
      ) : null}

      <DetailRow label="Reason">
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
          {request.reason || '—'}
        </Typography>
      </DetailRow>

      <DetailRow label="Status">
        <Chip
          size="small"
          label={formatLeaveStatus(request.status)}
          color={leaveStatusColor(request.status)}
        />
      </DetailRow>

      {request.createdAt || request.created_at ? (
        <DetailRow label="Submitted">
          <Typography variant="body2">{formatWhen(request.createdAt || request.created_at)}</Typography>
        </DetailRow>
      ) : null}

      {request.decidedAt || request.decided_at ? (
        <DetailRow label="Decision">
          <Typography variant="body2">
            {formatWhen(request.decidedAt || request.decided_at)}
            {request.decidedByName ? ` · ${request.decidedByName}` : ''}
          </Typography>
        </DetailRow>
      ) : null}

      {request.decisionNotes ? (
        <DetailRow label="Decision notes">
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {request.decisionNotes}
          </Typography>
        </DetailRow>
      ) : null}
    </Stack>
  );
}

export function LeaveRequestListCard({
  request,
  onViewDetails,
  showTeacher = false,
  authUser,
  headerActions = null,
  footer = null,
}) {
  const batches = request.batches || [];
  const teacherName = getLeaveTeacherName(request, showTeacher ? null : authUser);
  const leaveDate = formatLeaveDate(request.leaveDate || request.leave_date);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0,
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: 1 },
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }} sx={{ p: 2 }}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          {showTeacher && request.teacherName ? (
            teacherInitials(request.teacherName)
          ) : teacherName ? (
            teacherInitials(teacherName)
          ) : (
            <EventBusyIcon fontSize="small" />
          )}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {showTeacher && request.teacherName ? (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
              <PersonOutlineIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {request.teacherName}
              </Typography>
            </Stack>
          ) : teacherName ? (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.25 }}>
              {teacherName}
            </Typography>
          ) : null}

          <Typography variant="h6" component="div" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {leaveDate}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {formatLeaveScope(request.scope)}
            {batches.length > 0 ? ` · ${batches.map((b) => b.name).join(', ')}` : ''}
          </Typography>
          {request.reason ? (
            <Typography
              variant="body2"
              color="text.primary"
              sx={{
                mt: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {request.reason}
            </Typography>
          ) : null}
          {request.decisionNotes && request.status !== 'pending' ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              Supervisor: {request.decisionNotes}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0} flexWrap="wrap" useFlexGap>
          <Chip
            label={formatLeaveStatus(request.status)}
            color={leaveStatusColor(request.status)}
            size="small"
          />
          {onViewDetails ? (
            <Button size="small" variant="outlined" onClick={() => onViewDetails(request)}>
              View details
            </Button>
          ) : null}
          {headerActions}
        </Stack>
      </Stack>
      {footer ? (
        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
          {footer}
        </Box>
      ) : null}
    </Paper>
  );
}

export function LeaveRequestDetailsDialog({
  open,
  onClose,
  request,
  showTeacher = false,
  authUser,
}) {
  const teacherName = getLeaveTeacherName(request, showTeacher ? null : authUser);
  const titleName = showTeacher ? request?.teacherName : teacherName;
  const leaveDate = request ? formatLeaveDate(request.leaveDate || request.leave_date) : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="overline" color="text.secondary" display="block">
          Leave request
        </Typography>
        <Typography variant="h6" component="span" fontWeight={700}>
          {titleName ? `${titleName} · ` : ''}
          {leaveDate}
        </Typography>
        {request ? (
          <Box sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={formatLeaveStatus(request.status)}
              color={leaveStatusColor(request.status)}
            />
          </Box>
        ) : null}
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <LeaveRequestSummary request={request} showTeacher={showTeacher} authUser={authUser} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
